import {
  useCallback,
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'components/Modal/Modal';
import { NOTIFICATION_TABS } from 'constants/general';
import { formatMonthYear } from 'utils/dateFormatters';

import NotificationItem from './NotificationItem';
import NotificationsHeader from './NotificationsHeader';

import type INotification from 'store/slices/notifications/interfaces/INotification';
import type INotificationsModal from './interfaces/INotificationsModal';

import styles from './sass/NotificationsModal.module.scss';

function NotificationsModal({
  open,
  onClose,
  notifications,
  onMarkAllAsRead,
  onReadOneNotification,
  onGetNextNotification,
  isLoading,
  onStatusChange,
  hasNextPage,
  handleRedirectToTask,
}: INotificationsModal) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>(NOTIFICATION_TABS.ALL);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const groupedNotifications = useMemo(() => {
    if (!notifications) return {};

    const grouped: { [key: string]: INotification[] } = {};

    notifications.forEach((notification) => {
      const date = new Date(notification.createdAt);
      const monthYear = formatMonthYear(date);

      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(notification);
    });

    Object.keys(grouped).forEach((monthYear) => {
      grouped[monthYear].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    });

    return grouped;
  }, [notifications]);

  useEffect(() => {
    onStatusChange(activeTab === NOTIFICATION_TABS.ALL ? '' : activeTab);
  }, [activeTab, onStatusChange]);

  const handleScroll = useCallback(() => {
    if (!contentRef.current || !onGetNextNotification || !hasNextPage || isLoading) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const threshold = 100;

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      onGetNextNotification();
    }
  }, [onGetNextNotification, hasNextPage, isLoading]);

  useEffect(() => {
    const currentRef = contentRef.current;

    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);

      return () => {
        currentRef.removeEventListener('scroll', handleScroll);
      };
    }

    return undefined;
  }, [handleScroll]);

  return (
    <Modal
      open={open}
      title={t('common.notifications')}
      onClose={onClose}
      isButtonsVisible={false}
      className={styles.modalWrapper}
      closeClassName={styles.closeButton}
      titleClassName={styles.titleWrapper}
      isLoading={isLoading}
    >
      <div className={styles.root}>
        <NotificationsHeader
          activeTab={activeTab}
          onTabClick={handleTabClick}
          onMarkAllAsRead={onMarkAllAsRead}
        />
        <div className={styles.notificationsList} ref={contentRef}>
          {Object.entries(groupedNotifications).map(([monthYear, groupNotifications]) => (
            <div key={monthYear} className={styles.notificationGroup}>
              <div className={styles.monthHeader}>{monthYear}</div>
              {groupNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onReadOneNotification={onReadOneNotification}
                  handleRedirectToTask={handleRedirectToTask}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

export default NotificationsModal;
